<template>
  <div class="refund-payout-form">
    <div class="vx-col w-full px-0">
      <h6 class="mb-2">Dados bancários do segurado para restituição</h6>
    </div>

    <label v-if="shouldShowRefundPayoutDisclaimer"
      >Atenção: este endosso será restituído preferencialmente via cartão de
      crédito. Caso não seja possível realizar a restituição no cartão, ela será
      feita por transferência bancária para a conta informada abaixo.</label
    >

    <div class="vx-row">
      <div class="vx-col sm:w-1/3 w-full mb-2">
        <label for="Banco" class="vs-select--label"
          >Instituição Financeira</label
        >
        <ValidationProvider name="Banco" rules="required" v-slot="{ errors }">
          <v-select
            :options="refundBankOptions"
            id="Banco"
            v-model="localRefundData.bankId"
            :reduce="(option) => option.id"
            :danger="!!errors.length"
            data-testId="refund-data-bank-select"
            :disabled="disabled"
            :filterable="false"
            @search="$emit('search-bank', $event)"
          ></v-select>
          <span
            v-show="!!errors.length"
            class="text-danger"
            data-testId="refund-data-bank-select-error"
          >
            {{ errors[0] }}
          </span>
        </ValidationProvider>
      </div>

      <div
        class="vx-col flex flex-row sm:w-1/3 w-full mb-2 justify-between gap-4"
      >
        <div class="w-5/12">
          <label for="Agencia" class="vs-select--label">Agência</label>
          <ValidationProvider
            name="Agência"
            rules="required|max:4"
            v-slot="{ errors }"
          >
            <vs-input
              v-model="localRefundData.agency"
              class="w-full"
              id="Agencia"
              v-mask="'####'"
              :danger="!!errors.length"
              data-testId="refund-data-agency-select"
              :disabled="disabled"
            />
            <span
              v-show="!!errors.length"
              class="text-danger"
              data-testId="refund-data-agency-select-error"
            >
              {{ errors[0] }}
            </span>
          </ValidationProvider>
        </div>
        <div class="w-5/12">
          <label for="Conta Corrente" class="vs-select--label">
            Conta Corrente
          </label>
          <ValidationProvider
            name="Conta Corrente"
            rules="required|max:20"
            v-slot="{ errors }"
          >
            <vs-input
              v-model="localRefundData.account"
              class="w-full"
              id="Conta Corrente"
              v-mask="'####################'"
              :danger="!!errors.length"
              data-testId="refund-data-account-select"
              :disabled="disabled"
            />
            <span
              v-show="!!errors.length"
              class="text-danger"
              data-testId="refund-data-account-select-error"
            >
              {{ errors[0] }}
            </span>
          </ValidationProvider>
        </div>
        <div class="w-2/12">
          <label for="Digito" class="vs-select--label">Dígito</label>
          <ValidationProvider
            name="Dígito"
            rules="required|max:2"
            v-slot="{ errors }"
          >
            <vs-input
              v-model="localRefundData.accountDigit"
              class="w-full"
              id="Digito"
              v-mask="'#'"
              :danger="!!errors.length"
              data-testId="refund-data-account-digit-select"
              :disabled="disabled"
            />
            <span
              v-show="!!errors.length"
              class="text-danger"
              data-testId="refund-data-account-digit-select-error"
            >
              {{ errors[0] }}
            </span>
          </ValidationProvider>
        </div>
      </div>

      <div class="vx-col sm:w-1/3 w-full mb-2">
        <label for="Chave Pix" class="vs-select--label">Chave Pix</label>
        <ValidationProvider
          name="Chave Pix"
          rules="required"
          v-slot="{ errors }"
        >
          <vs-input
            v-model="localRefundData.pixKey"
            class="w-full"
            id="Chave Pix"
            :danger="!!errors.length"
            data-testId="refund-data-pix-key-select"
            :disabled="disabled"
          />
          <span
            v-show="!!errors.length"
            class="text-danger"
            data-testId="refund-data-pix-key-select-error"
          >
            {{ errors[0] }}
          </span>
        </ValidationProvider>
      </div>
    </div>

    <div class="vx-row">
      <div class="vx-col flex flex-row w-full mb-2 justify-star gap-4">
        <div class="w-full sm:w-1/4">
          <label for="Documento do Titular" class="vs-select--label">
            CPF/CNPJ do Titular
          </label>
          <ValidationProvider
            name="Documento do Titular"
            rules="required|cpf_cnpj_min|cpf_cnpj"
            v-slot="{ errors }"
          >
            <vs-input
              v-model="localRefundData.holderDocument"
              class="w-full"
              id="Documento do Titular"
              v-mask="[DOCUMENT_MASK.CPF_WITH_CNPJ, DOCUMENT_MASK.CNPJ]"
              @input="
                fieldsWithValidation.refundData.holderDocument =
                  $event.toUpperCase()
              "
              :danger="!!errors.length"
              data-testId="refund-data-holder-document"
              :disabled="disabled"
            />
            <span
              v-show="!!errors.length"
              class="text-danger"
              data-testId="refund-data-holder-document-error"
            >
              {{ errors[0] }}
            </span>
          </ValidationProvider>
        </div>
        <div class="w-full sm:w-1/3">
          <label for="Nome do Titular" class="vs-select--label">
            Nome do Titular
          </label>
          <ValidationProvider
            name="Nome do Titular"
            rules="required|nome"
            v-slot="{ errors }"
          >
            <vs-input
              v-model="localRefundData.holderName"
              class="w-full"
              id="Nome do Titular"
              :danger="!!errors.length"
              data-testId="refund-data-holder-name"
              :disabled="disabled"
            />
            <span
              v-show="!!errors.length"
              class="text-danger"
              data-testId="refund-data-holder-name-error"
            >
              {{ errors[0] }}
            </span>
          </ValidationProvider>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ValidationProvider } from 'vee-validate';
import vSelect from 'vue-select';
import { DOCUMENT_MASK } from '../../../../constants/cpfCnpj';

export default {
  name: 'RefundPayoutForm',
  components: {
    ValidationProvider,
    'v-select': vSelect,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    refundBankOptions: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    shouldShowRefundPayoutDisclaimer: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      DOCUMENT_MASK,
      localRefundData: this.getNormalizedRefundData(this.value),
    };
  },
  computed: {
    refundData() {
      return this.value || {};
    },
  },
  watch: {
    value: {
      handler(newValue) {
        const normalizedValue = this.getNormalizedRefundData(newValue);

        if (!this.isSameRefundData(normalizedValue, this.localRefundData)) {
          this.localRefundData = normalizedValue;
        }
      },
      deep: true,
    },
    localRefundData: {
      handler(newValue) {
        if (!this.isSameRefundData(newValue, this.refundData)) {
          this.$emit('input', { ...newValue });
        }
      },
      deep: true,
    },
  },
  methods: {
    getNormalizedRefundData(value) {
      return {
        bankId: value && value.bankId ? value.bankId : null,
        agency: value && value.agency ? value.agency : null,
        account: value && value.account ? value.account : null,
        accountDigit: value && value.accountDigit ? value.accountDigit : null,
        pixKey: value && value.pixKey ? value.pixKey : null,
        holderDocument:
          value && value.holderDocument ? value.holderDocument : null,
        holderName: value && value.holderName ? value.holderName : null,
      };
    },
    isSameRefundData(currentValue, nextValue) {
      return (
        currentValue.bankId === nextValue.bankId &&
        currentValue.agency === nextValue.agency &&
        currentValue.account === nextValue.account &&
        currentValue.accountDigit === nextValue.accountDigit &&
        currentValue.pixKey === nextValue.pixKey &&
        currentValue.holderDocument === nextValue.holderDocument &&
        currentValue.holderName === nextValue.holderName
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.refund-payout-form__description {
  color: #6b7280;
}

.refund-payout-form__method-selector {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
}
</style>
